import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Loader2, Inbox, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PageHeader({ title, subtitle, actions, icon: Icon }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold tracking-tight text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function DataTable({ columns, rows, loading, onRowClick, rowTestId, emptyText = "No records found" }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm ledger-table">
          <thead className="bg-muted/60 sticky top-0 z-10">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`text-left font-semibold text-xs uppercase tracking-wide text-muted-foreground px-4 py-3 whitespace-nowrap ${c.thClass || ""}`}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={columns.length} className="px-4 py-16 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-16 text-center">
                <Inbox className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">{emptyText}</p>
              </td></tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={row.id || i}
                  data-testid={rowTestId}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? "cursor-pointer transition-colors" : ""}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 align-middle ${c.tdClass || ""}`}>
                      {c.render ? c.render(row) : (row[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Pager({ page, limit, total, onPage }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
      <span>{total.toLocaleString("en-IN")} records · page {page} of {pages}</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev">
          <ChevronLeft className="h-4 w-4" /> Prev
        </Button>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pager-next">
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function useList(path, extraParams = {}) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const key = JSON.stringify(extraParams);

  const refetch = useCallback(() => {
    setLoading(true);
    api
      .get(path, { params: { page, limit: 25, ...extraParams } })
      .then(({ data }) => setData(Array.isArray(data) ? { items: data, total: data.length } : { items: data.items || [], total: data.total || 0 }))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [path, page, key]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => { setPage(1); }, [key]);

  return { ...data, loading, page, setPage, refetch };
}
